import { useCallback, useEffect, useState } from 'react'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import QuarterlyTaxEstimateDetailsForm from './components/QuarterlyTaxEstimateDetailsForm'
import QuarterlyTaxEstimateDetailsRow from './components/QuarterlyTaxEstimateDetailsRow'
import { selectAllQuarterlyTaxEstimateDetailsSorted } from './quarterlyTaxEstimateDetails.selector'
import {
  fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded,
  fetchAllQteDetailsKey,
  QuarterlyTaxEstimateDetail,
} from './quarterlyTaxEstimateDetails.slice'
import { useReselector } from '@/utils/sharedHooks'
import {
  Alert,
  Button,
  Card,
  Loader,
  Dialog,
  Table,
  Text,
  Separator,
} from '@/ui'
import { fetchAllUsersIfNeeded } from '@/actions/admin/adminAllUsersActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

const SORT_PARAMS = { sortBy: 'taxYear', sortOrder: 'desc' } as const

export const QuarterlyTaxEstimateDetailsPanel = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchAdminAllQuarterlyTaxEstimateDetailsIfNeeded(SORT_PARAMS))
    dispatch(fetchAllUsersIfNeeded())
  }, [dispatch])

  const [qteEdit, setQteEdit] = useState<QuarterlyTaxEstimateDetail>()
  const [modalOpen, setModalOpen] = useState(false)

  const allQuarterlyTaxEstimateDetailsSorted = useReselector(
    selectAllQuarterlyTaxEstimateDetailsSorted
  )
  const loading = useReselector(
    getIsFetchingOrNotStarted,
    fetchAllQteDetailsKey(SORT_PARAMS)
  )
  const error = useReselector(getFetchError, fetchAllQteDetailsKey(SORT_PARAMS))

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setQteEdit(undefined)
  }, [])

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Text variant={TypographyStyles.H2}>
          Quarterly Tax Estimate Details
        </Text>
        {loading && <Loader active inline />}
        {!loading && !error && (
          <>
            <Button variant="secondary" onClick={() => setModalOpen(true)}>
              Create a future entry
            </Button>
            <Alert title="All dates are inclusive">
              Changing values can impact QTE calculations & Tax Center content.
              Please double-check your changes before applying.
            </Alert>
            <Table className="mb-8">
              <Table.Header sticky className="bg-neutral-0">
                <Table.Row>
                  <Table.HeaderCell />
                  <Table.HeaderCell>Tax Year</Table.HeaderCell>
                  <Table.HeaderCell>Tax Quarter</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>IRS Quarter Start</Table.HeaderCell>
                  <Table.HeaderCell>IRS Quarter End</Table.HeaderCell>
                  <Table.HeaderCell>IRS Payment Due</Table.HeaderCell>
                  <Table.HeaderCell>New User Cut-Off</Table.HeaderCell>
                  <Table.HeaderCell>Checklist Launch Date</Table.HeaderCell>
                  <Table.HeaderCell>Calculation Starts</Table.HeaderCell>
                  <Table.HeaderCell>Client Notified</Table.HeaderCell>
                  <Table.HeaderCell>Bookkeeping Period Start</Table.HeaderCell>
                  <Table.HeaderCell>Bookkeeping Period End</Table.HeaderCell>
                  <Table.HeaderCell>
                    Bookkeeping Period Total Months
                  </Table.HeaderCell>
                  <Table.HeaderCell>Edge Case User IDs</Table.HeaderCell>
                  <Table.HeaderCell>Last Updated</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              {allQuarterlyTaxEstimateDetailsSorted.map((details) => (
                <QuarterlyTaxEstimateDetailsRow
                  onEditClick={() => {
                    setModalOpen(true)
                    setQteEdit(details)
                  }}
                  key={`detail-${details.taxQuarter}-${details.taxYear}`}
                  details={details}
                />
              ))}
            </Table>
          </>
        )}
        {error && (
          <Alert
            variant="error"
            title="An error occurred, refresh page to try again."
          >
            <Text>{error.message}</Text>
          </Alert>
        )}
      </div>
      <Dialog open={modalOpen} onClose={closeModal}>
        <Dialog.Content>
          <Text variant={TypographyStyles.H3}>
            {qteEdit
              ? `Update ${qteEdit.taxYear} Q${qteEdit.taxQuarter}`
              : 'Create a future entry'}
          </Text>
          <Separator />
          <QuarterlyTaxEstimateDetailsForm
            onCancel={() => setModalOpen(false)}
            details={qteEdit}
          />
        </Dialog.Content>
      </Dialog>
    </Card>
  )
}
